/**
 * useRoles — Hook for role management CRUD operations
 */

import { useState, useCallback, useEffect } from "react";
import useApi from "./useApi";
import { endpoints } from "config/api";
import logger from "services/logger";

export default function useRoles({ autoFetch = false } = {}) {
  const { get, post, put, del } = useApi();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get(endpoints.roles.base);
      setRoles(data.roles || []);
      return data.roles;
    } catch (err) {
      logger.error("[useRoles]", "Failed to fetch roles:", err);
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [get]);

  const getRole = useCallback(
    async (id) => {
      try {
        const data = await get(endpoints.roles.byId(id));
        return data.role;
      } catch (err) {
        logger.error("[useRoles]", `Failed to fetch role ${id}:`, err);
        throw err;
      }
    },
    [get]
  );

  const createRole = useCallback(
    async (roleData) => {
      try {
        const data = await post(endpoints.roles.base, roleData);
        setRoles((prev) => [...prev, data.role]);
        return data.role;
      } catch (err) {
        logger.error("[useRoles]", "Failed to create role:", err);
        throw err;
      }
    },
    [post]
  );

  const updateRole = useCallback(
    async (id, roleData) => {
      try {
        const data = await put(endpoints.roles.byId(id), roleData);
        setRoles((prev) => prev.map((r) => (r.id === id ? data.role : r)));
        return data.role;
      } catch (err) {
        logger.error("[useRoles]", `Failed to update role ${id}:`, err);
        throw err;
      }
    },
    [put]
  );

  const deleteRole = useCallback(
    async (id) => {
      try {
        await del(endpoints.roles.byId(id));
        setRoles((prev) => prev.filter((r) => r.id !== id));
      } catch (err) {
        logger.error("[useRoles]", `Failed to delete role ${id}:`, err);
        throw err;
      }
    },
    [del]
  );

  useEffect(() => {
    if (autoFetch) {
      fetchRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch]);

  return {
    roles,
    loading,
    error,
    fetchRoles,
    getRole,
    createRole,
    updateRole,
    deleteRole,
  };
}
